import * as React from "react";
import { skipRefocusAfterPointer } from "../../lib/focus";
import { cn } from "../../lib/utils";
import { Menubar as MenubarPrimitive } from "radix-ui";

/**
 * メニューバー。メニューが開いている間に、となりのメニューへ移ると(ホバー・クリックとも)すぐ切り替わる。
 * Chrome のブックマークバーのフォルダのように使う(DropdownMenu を並べると、開いているメニューを
 * 閉じるクリックが 1 回余計に要る)。
 */
function Menubar({ className, ...props }: React.ComponentProps<typeof MenubarPrimitive.Root>) {
  return (
    <MenubarPrimitive.Root
      data-slot="menubar"
      className={cn("flex items-center gap-0.5", className)}
      {...props}
    />
  );
}

function MenubarMenu({ ...props }: React.ComponentProps<typeof MenubarPrimitive.Menu>) {
  return <MenubarPrimitive.Menu data-slot="menubar-menu" {...props} />;
}

function MenubarTrigger({
  className,
  ...props
}: React.ComponentProps<typeof MenubarPrimitive.Trigger>) {
  return (
    <MenubarPrimitive.Trigger
      data-slot="menubar-trigger"
      className={cn(
        "inline-flex items-center rounded-md text-sm font-medium outline-none select-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 data-[state=open]:bg-accent",
        className,
      )}
      {...props}
    />
  );
}

function MenubarContent({
  className,
  align = "start",
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof MenubarPrimitive.Content>) {
  return (
    <MenubarPrimitive.Portal>
      <MenubarPrimitive.Content
        data-slot="menubar-content"
        align={align}
        sideOffset={sideOffset}
        // マウスで閉じたときは、ボタンへフォーカスを戻さない(lib/focus.ts)
        onCloseAutoFocus={skipRefocusAfterPointer}
        // 開いたまま、となりのメニューへ移ると、フォーカスが新しいメニューの中へ入る。閉じかけの前のメニューが
        // それを「外側へのフォーカス」と見なして全体を閉じてしまうので、別のメニューの中への移動は無視する
        onFocusOutside={(e) => {
          if ((e.target as HTMLElement | null)?.closest?.("[data-radix-menubar-content]"))
            e.preventDefault();
        }}
        className={cn(
          "z-50 min-w-[8rem] origin-(--radix-menubar-content-transform-origin) overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
          className,
        )}
        {...props}
      />
    </MenubarPrimitive.Portal>
  );
}

function MenubarItem({ className, ...props }: React.ComponentProps<typeof MenubarPrimitive.Item>) {
  return (
    <MenubarPrimitive.Item
      data-slot="menubar-item"
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

export { Menubar, MenubarContent, MenubarItem, MenubarMenu, MenubarTrigger };
